import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SessionSettings, User } from '../types';
import { heartbeatService } from './heartbeatService';

export interface SessionData {
  id: string;
  name: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastActivityAt: Timestamp;
  isActive: boolean;
  maxUsers: number;
  currentUsers: string[]; // Array of user IDs
  settings: SessionSettings;
}

export interface SessionUser extends User {
  joinedAt: Timestamp;
  lastSeenAt: Timestamp;
  isActive: boolean;
}

class SessionService {
  private activeSessions: Map<string, () => void> = new Map(); // Unsubscribe functions

  // Create a new session
  async createSession(
    name: string,
    createdBy: string,
    maxUsers: number = 5,
    settings: SessionSettings
  ): Promise<string> {
    try {
      const sessionData: Omit<SessionData, 'id'> = {
        name,
        createdBy,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        lastActivityAt: serverTimestamp() as Timestamp,
        isActive: true,
        maxUsers,
        currentUsers: [createdBy],
        settings
      };

      const docRef = await addDoc(collection(db, 'sessions'), sessionData);
      
      // Create session users subcollection
      await this.addUserToSession(docRef.id, createdBy, true);
      
      return docRef.id;
    } catch (error) {
      throw new Error(`Failed to create session: ${error}`);
    }
  }

  // Create a new session with specific ID
  async createSessionWithId(
    sessionId: string,
    sessionData: {
      name: string;
      createdBy: string;
      maxUsers: number;
      settings: SessionSettings;
    }
  ): Promise<void> {
    try {
      const firestoreData: Omit<SessionData, 'id'> = {
        name: sessionData.name,
        createdBy: sessionData.createdBy,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        lastActivityAt: serverTimestamp() as Timestamp,
        isActive: true,
        maxUsers: sessionData.maxUsers,
        currentUsers: [sessionData.createdBy],
        settings: sessionData.settings,
      };

      const sessionRef = doc(db, 'sessions', sessionId);
      await setDoc(sessionRef, firestoreData);
      
      // Set creator's online status
      const userRef = doc(db, 'users', sessionData.createdBy);
      await setDoc(userRef, {
        isOnline: true,
        lastSeenAt: serverTimestamp()
      }, { merge: true });
      
      // Create session users subcollection
      await this.addUserToSession(sessionId, sessionData.createdBy, true);
    } catch (error) {
      throw new Error(`Failed to create session: ${error}`);
    }
  }

  // Join a session
  async joinSession(sessionId: string, userId: string): Promise<SessionData> {
    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      
      const sessionData = sessionDoc.data() as SessionData;
      
      // If session is inactive, reactivate it
      if (!sessionData.isActive) {
        await updateDoc(sessionRef, {
          isActive: true,
          lastActivityAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // Check if session is full
      if (sessionData.currentUsers.length >= sessionData.maxUsers) {
        throw new Error('Session is full');
      }
      
      // Check if user is already in session
      if (sessionData.currentUsers.includes(userId)) {
        // User is already in session, but ensure they're properly set up
        await this.addUserToSession(sessionId, userId, false);
        
        // Update user's online status
        const userRef = doc(db, 'users', userId);
        await setDoc(userRef, {
          isOnline: true,
          lastSeenAt: serverTimestamp()
        }, { merge: true });
        
        // Start heartbeat for this user
        heartbeatService.startHeartbeat(userId, sessionId);
        
        return { ...sessionData, id: sessionId };
      }
      
      // Check if session requires approval
      if (sessionData.settings.requireApproval) {
        // Add user to pending approvals instead of directly to session
        await this.addUserToPendingApprovals(sessionId, userId);
        throw new Error('JOIN_REQUEST_PENDING_APPROVAL');
      }
      
      // Add user to session (no approval required)
      await this.addUserToSession(sessionId, userId, false);
      
      // Update user's online status
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        isOnline: true,
        lastSeenAt: serverTimestamp()
      }, { merge: true });
      
      // Update session - use setDoc with merge to avoid conflicts
      await setDoc(sessionRef, {
        currentUsers: [...sessionData.currentUsers, userId],
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // Start heartbeat for this user
      heartbeatService.startHeartbeat(userId, sessionId);
      
      return { ...sessionData, id: sessionId };
    } catch (error) {
      console.error('Join session error:', error);
      throw new Error(`Failed to join session: ${error}`);
    }
  }

  // Leave a session
  async leaveSession(sessionId: string, userId: string): Promise<void> {
    try {
      // Stop heartbeat first
      heartbeatService.stopHeartbeat();
      
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      
      const sessionData = sessionDoc.data() as SessionData;
      
      // Remove user from session
      await this.removeUserFromSession(sessionId, userId);
      
      // Update user's online status
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        isOnline: false,
        lastSeenAt: serverTimestamp()
      }, { merge: true });
      
      // Update session
      const updatedUsers = sessionData.currentUsers.filter(id => id !== userId);
      
      if (updatedUsers.length === 0) {
        // If no users left, mark session as inactive (will be cleaned up after 1 hour)
        await updateDoc(sessionRef, {
          isActive: false,
          lastActivityAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Update current users list - session remains active
        await updateDoc(sessionRef, {
          currentUsers: updatedUsers,
          lastActivityAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      throw new Error(`Failed to leave session: ${error}`);
    }
  }

  // Add user to session
  private async addUserToSession(sessionId: string, userId: string, isCreator: boolean = false): Promise<void> {
    const userRef = doc(db, 'sessions', sessionId, 'session_users', userId);
    await setDoc(userRef, {
      joinedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
      isActive: true,
      isCreator
    });
  }

  // Add user to pending approvals
  private async addUserToPendingApprovals(sessionId: string, userId: string): Promise<void> {
    try {
      const approvalRef = doc(db, 'pending_approvals', `${sessionId}_${userId}`);
      await setDoc(approvalRef, {
        sessionId,
        userId,
        requestedAt: serverTimestamp(),
        status: 'pending'
      });
    } catch (error) {
      console.error('Add user to pending approvals error:', error);
      throw new Error(`Failed to add user to pending approvals: ${error}`);
    }
  }

  // Approve user to join session
  async approveUserJoin(sessionId: string, userId: string, approvedBy: string): Promise<void> {
    try {
      // Remove from pending approvals
      const approvalRef = doc(db, 'pending_approvals', `${sessionId}_${userId}`);
      await deleteDoc(approvalRef);

      // Get current session data
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      
      const sessionData = sessionDoc.data() as SessionData;
      
      // Check if user is already in session
      if (sessionData.currentUsers.includes(userId)) {
        console.log(`User ${userId} is already in session ${sessionId}`);
        return;
      }

      // Add user to session_users subcollection
      await this.addUserToSession(sessionId, userId, false);

      // Update user's online status
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        isOnline: true,
        lastSeenAt: serverTimestamp()
      }, { merge: true });

      // Update session with new user
      await setDoc(sessionRef, {
        currentUsers: [...sessionData.currentUsers, userId],
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Start heartbeat for the approved user
      heartbeatService.startHeartbeat(userId, sessionId);

    } catch (error) {
      console.error('Approve user join error:', error);
      throw new Error(`Failed to approve user join: ${error}`);
    }
  }

  // Reject user join request
  async rejectUserJoin(sessionId: string, userId: string, rejectedBy: string): Promise<void> {
    try {
      const approvalRef = doc(db, 'pending_approvals', `${sessionId}_${userId}`);
      await deleteDoc(approvalRef);
    } catch (error) {
      console.error('Reject user join error:', error);
      throw new Error(`Failed to reject user join: ${error}`);
    }
  }

  // Listen for pending approval requests
  listenForPendingApprovals(sessionId: string, callback: (approvals: any[]) => void): () => void {
    const approvalsRef = collection(db, 'pending_approvals');
    const q = query(approvalsRef, where('sessionId', '==', sessionId), where('status', '==', 'pending'));
    
    return onSnapshot(q, (snapshot) => {
      const approvals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(approvals);
    });
  }

  // Save user profile to users collection
  async saveUserProfile(user: User): Promise<void> {
    try {
      const userRef = doc(db, 'users', user.id);
      await setDoc(userRef, {
        name: user.name,
        color: user.color,
        avatar: user.avatar,
        isOnline: user.isOnline,
        isAnonymous: user.isAnonymous || false,
        lastSeenAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to save user profile:', error);
    }
  }

  // Remove user from session
  private async removeUserFromSession(sessionId: string, userId: string): Promise<void> {
    const userRef = doc(db, 'sessions', sessionId, 'session_users', userId);
    await deleteDoc(userRef);
  }

  // Get session by ID
  async getSession(sessionId: string): Promise<SessionData | null> {
    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        return null;
      }
      
      return { ...sessionDoc.data() as SessionData, id: sessionId };
    } catch (error) {
      throw new Error(`Failed to get session: ${error}`);
    }
  }

  // Get active sessions
  async getActiveSessions(limitCount: number = 20): Promise<SessionData[]> {
    try {
      const q = query(
        collection(db, 'sessions'),
        where('isActive', '==', true),
        orderBy('lastActivityAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data() as SessionData,
        id: doc.id
      }));
    } catch (error) {
      throw new Error(`Failed to get active sessions: ${error}`);
    }
  }

  // Get session users
  async getSessionUsers(sessionId: string): Promise<SessionUser[]> {
    try {
      const q = query(
        collection(db, 'sessions', sessionId, 'users'),
        where('isActive', '==', true),
        orderBy('joinedAt', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const users: SessionUser[] = [];
      
      for (const userDoc of querySnapshot.docs) {
        const userData = userDoc.data();
        // Get user details from users collection
        const userDetailsRef = doc(db, 'users', userData.userId);
        const userDetailsDoc = await getDoc(userDetailsRef);
        
        if (userDetailsDoc.exists()) {
          const userDetails = userDetailsDoc.data();
          users.push({
            id: userData.userId,
            name: userDetails.name,
            color: userDetails.color,
            avatar: userDetails.avatar,
            isOnline: userDetails.isOnline,
            isTyping: false,
            isAnonymous: userDetails.isAnonymous,
            joinedAt: userData.joinedAt,
            lastSeenAt: userData.lastSeenAt,
            isActive: userData.isActive
          });
        }
      }
      
      return users;
    } catch (error) {
      throw new Error(`Failed to get session users: ${error}`);
    }
  }

  // Update session settings
  async updateSessionSettings(sessionId: string, settings: Partial<SessionSettings>): Promise<void> {
    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      // Use setDoc with merge to avoid conflicts and ensure the document exists
      await setDoc(sessionRef, {
        settings: settings,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Update session settings error:', error);
      throw new Error(`Failed to update session settings: ${error}`);
    }
  }

  // Update session activity
  async updateSessionActivity(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, 'sessions', sessionId);
      await updateDoc(sessionRef, {
        lastActivityAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  }

  // Listen to session changes
  onSessionChange(sessionId: string, callback: (session: SessionData | null) => void): () => void {
    const sessionRef = doc(db, 'sessions', sessionId);
    
    const unsubscribe = onSnapshot(sessionRef, (doc) => {
      if (doc.exists()) {
        callback({ ...doc.data() as SessionData, id: doc.id });
      } else {
        callback(null);
      }
    });
    
    // Store unsubscribe function
    this.activeSessions.set(sessionId, unsubscribe);
    
    return unsubscribe;
  }

  // Listen to session users changes
  onSessionUsersChange(sessionId: string, callback: (users: SessionUser[]) => void): () => void {
    const q = query(
      collection(db, 'sessions', sessionId, 'users'),
      where('isActive', '==', true),
      orderBy('joinedAt', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const users: SessionUser[] = [];
      
      for (const userDoc of querySnapshot.docs) {
        const userData = userDoc.data();
        // Get user details from users collection
        const userDetailsRef = doc(db, 'users', userData.userId);
        const userDetailsDoc = await getDoc(userDetailsRef);
        
        if (userDetailsDoc.exists()) {
          const userDetails = userDetailsDoc.data();
          users.push({
            id: userData.userId,
            name: userDetails.name,
            color: userDetails.color,
            avatar: userDetails.avatar,
            isOnline: userDetails.isOnline,
            isTyping: false,
            isAnonymous: userDetails.isAnonymous,
            joinedAt: userData.joinedAt,
            lastSeenAt: userData.lastSeenAt,
            isActive: userData.isActive
          });
        }
      }
      
      callback(users);
    });
    
    return unsubscribe;
  }

  // Clean up inactive sessions (called by cloud function)
  async cleanupInactiveSessions(): Promise<void> {
    try {
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - 24); // 24 hours ago
      
      const q = query(
        collection(db, 'sessions'),
        where('isActive', '==', true),
        where('lastActivityAt', '<', Timestamp.fromDate(cutoffTime))
      );
      
      const querySnapshot = await getDocs(q);
      
      for (const doc of querySnapshot.docs) {
        await updateDoc(doc.ref, {
          isActive: false,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Failed to cleanup inactive sessions:', error);
    }
  }

  // Listen for session changes
  listenForSessionChanges(
    sessionId: string,
    callback: (session: SessionData | null) => void
  ): () => void {
    const sessionRef = doc(db, 'sessions', sessionId);
    
    const unsubscribe = onSnapshot(sessionRef, (doc) => {
      if (doc.exists()) {
        const sessionData = { id: doc.id, ...doc.data() } as SessionData;
        callback(sessionData);
      } else {
        callback(null);
      }
    });
    
    return unsubscribe;
  }

  // Listen for session users
  listenForSessionUsers(
    sessionId: string,
    callback: (users: SessionUser[]) => void
  ): () => void {
    const sessionUsersRef = collection(db, 'sessions', sessionId, 'session_users');
    
    const unsubscribe = onSnapshot(sessionUsersRef, 
      async (querySnapshot) => {
        const users: SessionUser[] = [];
        
        for (const userDoc of querySnapshot.docs) {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Get user details from users collection
          const userDetailsRef = doc(db, 'users', userId);
          const userDetailsDoc = await getDoc(userDetailsRef);
          
          if (userDetailsDoc.exists()) {
            const userDetails = userDetailsDoc.data();
            // Only include users who are currently online
            if (userDetails.isOnline) {
              users.push({
                id: userId,
                name: userDetails.name,
                color: userDetails.color,
                avatar: userDetails.avatar,
                isOnline: userDetails.isOnline,
                isTyping: false,
                isAnonymous: userDetails.isAnonymous,
                joinedAt: userData.joinedAt,
                lastSeenAt: userData.lastSeenAt,
                isActive: userData.isActive
              });
            }
          }
        }
        
        callback(users);
      },
      (error) => {
        console.error('Session users listener error:', error);
        // Attempt to reconnect after a delay
        setTimeout(() => {
          console.log('Attempting to reconnect session users listener...');
          this.listenForSessionUsers(sessionId, callback);
        }, 5000);
      }
    );
    
    return unsubscribe;
  }

  // Clean up all listeners
  cleanup(): void {
    this.activeSessions.forEach(unsubscribe => unsubscribe());
    this.activeSessions.clear();
  }
}

// Export singleton instance
export const sessionService = new SessionService();
export default sessionService;
